import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, Lock } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов генератора
import HashForm from './BcryptHashGeneratorPage/components/HashForm';
import ResultsView from './BcryptHashGeneratorPage/components/ResultsView';
import HashHistoryList from './BcryptHashGeneratorPage/components/HashHistoryList';
import BcryptInfoContent from './BcryptHashGeneratorPage/components/BcryptInfoContent';
import OptionsPanel from './BcryptHashGeneratorPage/components/OptionsPanel';
import SecurityTipsSidebar from './BcryptHashGeneratorPage/components/SecurityTipsSidebar';
import PasswordValidationSidebar from './BcryptHashGeneratorPage/components/PasswordValidationSidebar';
import UsageSidebar from './BcryptHashGeneratorPage/components/UsageSidebar';

// Определение начального состояния формы
export const initialFormData = {
  plaintext: '',
  rounds: 10,
  hashFormat: 'bcrypt',
  autoGenerate: false,
  passwordLength: 12,
  includeLowercase: true,
  includeUppercase: true,
  includeNumbers: true,
  includeSymbols: true,
  saveHistory: true,
};

/**
 * BcryptHashGeneratorPage Component - Полный генератор Bcrypt-хешей с опциями настройки
 * и информацией о безопасности.
 */
const BcryptHashGeneratorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('bcrypt-hash-generator'), []);
  const pageTitle = tool?.metaTitle || 'Генератор Bcrypt-хешей | Gensio';
  const metaDescription = tool?.metaDescription || 'Создавайте безопасные Bcrypt-хеши для паролей с настраиваемым количеством раундов. Инструмент для разработчиков и администраторов баз данных для реализации надежной защиты учетных записей пользователей.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [result, setResult] = useState(null);
  const [hashHistory, setHashHistory] = useState([]);
  const [errors, setErrors] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [validateResult, setValidateResult] = useState(null);
  const [showHashInfo, setShowHashInfo] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка истории хешей из localStorage ---
  useEffect(() => {
    const savedHistory = localStorage.getItem('bcryptHashHistory');
    if (savedHistory) {
      try {
        setHashHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error("Ошибка загрузки истории хешей:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'default':
          // Стандартные настройки
          break;
        case 'strong':
          newState.rounds = 12;
          break;
        case 'password-generator':
          newState.autoGenerate = true;
          newState.passwordLength = 16;
          break;
        case 'hash-validator':
          // Для валидатора
          newState.rounds = 10;
          setShowHashInfo(true);
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResult(null);
    setValidateResult(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение хеша в историю
  const saveHash = (hashResult) => {
    if (!formData.saveHistory || !hashResult) return;

    const newHistoryItem = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      plaintext: hashResult.plaintext,
      hash: hashResult.hash,
      rounds: hashResult.rounds,
      format: hashResult.format,
      isGenerated: hashResult.isGenerated
    };

    const updatedHistory = [newHistoryItem, ...hashHistory.slice(0, 9)]; // Сохраняем только 10 последних хешей
    setHashHistory(updatedHistory);
    localStorage.setItem('bcryptHashHistory', JSON.stringify(updatedHistory));

    return newHistoryItem;
  };

  // Удаление хеша из истории
  const deleteHistoryItem = (id) => {
    const updatedHistory = hashHistory.filter(item => item.id !== id);
    setHashHistory(updatedHistory);
    localStorage.setItem('bcryptHashHistory', JSON.stringify(updatedHistory));
  };

  // Очистка всей истории
  const clearHistory = () => {
    setHashHistory([]);
    localStorage.removeItem('bcryptHashHistory');
  };

  // Использование хеша из истории
  const useHistoryItem = (id) => {
    const item = hashHistory.find(i => i.id === id);
    if (item) {
      setFormData(prev => ({
        ...prev,
        plaintext: item.plaintext,
        rounds: item.rounds,
        hashFormat: item.format
      }));
      setResult({
        plaintext: item.plaintext,
        hash: item.hash,
        rounds: item.rounds,
        format: item.format,
        isGenerated: item.isGenerated,
        timestamp: new Date().toISOString()
      });
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                {tool?.name || 'Генератор Bcrypt-хешей'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Надежный инструмент для хеширования паролей с использованием алгоритма Bcrypt — стандарта отрасли для безопасного хранения аутентификационных данных.
              </p>
            </div>

            {/* --- Форма генерации хеша --- */}
            <HashForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isGenerating={isGenerating}
              setIsGenerating={setIsGenerating}
              result={result}
              setResult={setResult}
              validateResult={validateResult}
              setValidateResult={setValidateResult}
              saveHash={saveHash}
            />

            {/* --- Результаты генерации --- */}
            {result && !isGenerating && (
              <ResultsView 
                result={result} 
                validateResult={validateResult}
              />
            )}

            {/* --- История хешей --- */}
            {hashHistory.length > 0 && (
              <HashHistoryList 
                hashHistory={hashHistory}
                applyHistoryItem={useHistoryItem}
                deleteHistoryItem={deleteHistoryItem}
                clearHistory={clearHistory}
              />
            )}

            {/* --- Информационный контент о Bcrypt --- */}
            <BcryptInfoContent 
              showHashInfo={showHashInfo}
              setShowHashInfo={setShowHashInfo}
            />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель опций --- */}
            <OptionsPanel 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isGenerating={isGenerating}
            />

            {/* --- Панель советов по безопасности --- */}
            <SecurityTipsSidebar />

            {/* --- Панель валидации пароля --- */}
            <PasswordValidationSidebar 
              formData={formData}
            />

            {/* --- Панель примеров использования --- */}
            <UsageSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Bcrypt Hash Highlighting */
        .hash-highlight {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          @apply text-xs sm:text-sm bg-gray-100 dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700 overflow-x-auto whitespace-nowrap;
        }
        .hash-prefix {
          @apply text-blue-600 dark:text-blue-400;
        }
        .hash-salt {
          @apply text-green-600 dark:text-green-400;
        }
        .hash-checksum {
          @apply text-purple-600 dark:text-purple-400;
        }
        
        /* Animation for hash generation */
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        .animate-pulse-slow {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
};

export default BcryptHashGeneratorPage;